import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Calendar, Trash2, Check, MapPin, Container as ContainerIcon, AlertTriangle, RotateCcw, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import ScanModal from "@/components/ScanModal";
import ModuleTabs from "@/components/ModuleTabs";

const TYPE_OPTIONS = ["20STD", "40STD", "40HC", "40RF", "20OT", "20RF", "40OT"];
const LOCATION_OPTIONS = ["MPS", "GPHA"];

function todayIso() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function fmt(n) { return n === null || n === undefined ? "—" : Number(n).toLocaleString(); }

export default function Entry() {
  const [date, setDate] = useState(todayIso());
  const [containerNo, setContainerNo] = useState("");
  const [typeSize, setTypeSize] = useState("40HC");
  const [location, setLocation] = useState("MPS");
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]); // last 10 added in this session
  const [todayStats, setTodayStats] = useState({ count: 0, usd: 0, ghc: 0 });
  const [rates, setRates] = useState({ inspection_charge_usd: 4, toll_vann_ghc: 35 });
  const [toast, setToast] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const inputRef = useRef(null);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2200);
  };

  const fetchRates = useCallback(async () => {
    try {
      const r = await api.get("/settings/rates");
      setRates(r.data);
    } catch (e) { console.warn("fetchRates failed:", e?.message); }
  }, []);

  const fetchTodayStats = useCallback(async (forDate) => {
    try {
      const r = await api.get("/containers", { params: { date_from: forDate, date_to: forDate, limit: 2000 } });
      const items = r.data.items;
      const usd = items.reduce((a, b) => a + (b.inspection_charge_usd || 0), 0);
      const ghc = items.reduce((a, b) => a + (b.toll_vann_ghc || 0), 0);
      setTodayStats({ count: items.length, usd, ghc });
    } catch (e) { console.warn("fetchTodayStats failed:", e?.message); }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);
  useEffect(() => { fetchTodayStats(date); }, [date, fetchTodayStats]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const cleaned = containerNo.trim().toUpperCase();
    if (!cleaned) { showToast("Container number is required", "err"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/containers", {
        date,
        container_no: cleaned,
        type_size: typeSize,
        location,
        // omit rates → backend uses fixed yearly settings
      });
      const created = r.data;
      setRecent((prev) => [created, ...prev].slice(0, 10));
      setContainerNo("");
      showToast(`Added ${cleaned}`);
      // Refresh today's totals
      fetchTodayStats(date);
      // Refocus
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed to add", "err");
    } finally {
      setSubmitting(false);
    }
  };

  const undoLast = async (row) => {
    try {
      await api.delete(`/containers/${row.id}`);
      setRecent((prev) => prev.filter((r) => r.id !== row.id));
      showToast(`Removed ${row.container_no}`);
      fetchTodayStats(date);
    } catch (e) {
      showToast("Undo failed", "err");
    }
  };

  return (
    <div className="space-y-6 pb-24" data-testid="entry-page">
      <ModuleTabs module="inspection" />
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="overline mb-2">Gate In</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight leading-tight">
            Container just inspected?
          </h1>
          <p className="mt-2 text-slate-600">Tap, type, done. Charges applied automatically.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="px-2 py-1 rounded bg-slate-100 font-mono-tabular">USD {rates.inspection_charge_usd}</span>
          <span className="px-2 py-1 rounded bg-slate-100 font-mono-tabular">GHC {rates.toll_vann_ghc}</span>
          <span className="text-[10px] uppercase tracking-wider">/ container</span>
        </div>
      </div>

      {/* Today's running tally */}
      <div className="grid grid-cols-3 gap-3" data-testid="today-stats">
        <TallyCard label={`${date === todayIso() ? "Today" : date}`} value={fmt(todayStats.count)} sub="containers" accent="#0a2952" />
        <TallyCard label="Inspection" value={`USD ${fmt(todayStats.usd)}`} sub={`@ USD ${rates.inspection_charge_usd}`} accent="#ff5b2c" />
        <TallyCard label="Toll & Vann" value={`GHC ${fmt(todayStats.ghc)}`} sub={`@ GHC ${rates.toll_vann_ghc}`} accent="#14b8a6" />
      </div>

      {/* Quick entry form */}
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-5 sm:p-6 space-y-5" data-testid="entry-form">
        {/* Date row */}
        <div className="flex items-center gap-3">
          <Calendar size={16} className="text-slate-400" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="entry-date"
            className="form-input max-w-[200px]"
          />
          <button
            type="button"
            onClick={() => setDate(todayIso())}
            data-testid="entry-today-btn"
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded transition-colors ${
              date === todayIso() ? "bg-[#0a2952] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Today
          </button>
        </div>

        {/* Container number — big, focus, monospace */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="overline">Container number</label>
            <button
              type="button"
              onClick={() => setScanOpen(true)}
              data-testid="entry-scan-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-[#0a2952] hover:bg-[#071f40] text-white px-3 py-1.5 rounded transition-colors"
            >
              <Camera size={13} /> Scan
            </button>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={containerNo}
            onChange={(e) => setContainerNo(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
            placeholder="HLBU0000000"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={11}
            data-testid="entry-container-no"
            className="w-full px-4 py-4 text-2xl sm:text-3xl font-mono-tabular font-bold tracking-wider text-[#0a2952] uppercase border-2 border-slate-300 rounded-md focus:border-[#ff5b2c] focus:ring-2 focus:ring-[#ff5b2c]/20 outline-none placeholder:text-slate-300 placeholder:font-normal placeholder:tracking-widest"
          />
          <div className="mt-1.5 text-[11px] text-slate-400 font-mono-tabular">{containerNo.length}/11</div>
        </div>

        {/* Type/Size as button grid */}
        <div>
          <label className="overline mb-2 block flex items-center gap-2">
            <ContainerIcon size={12} /> Type / Size
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="entry-type-buttons">
            {TYPE_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`entry-type-${t}`}
                onClick={() => setTypeSize(t)}
                className={`px-3 py-3 rounded-md font-mono-tabular font-bold text-sm transition-all ${
                  typeSize === t
                    ? "bg-[#0a2952] text-white shadow-md scale-[1.02]"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Location as 2 big buttons */}
        <div>
          <label className="overline mb-2 block flex items-center gap-2">
            <MapPin size={12} /> Location
          </label>
          <div className="grid grid-cols-2 gap-2" data-testid="entry-location-buttons">
            {LOCATION_OPTIONS.map((l) => (
              <button
                key={l}
                type="button"
                data-testid={`entry-location-${l}`}
                onClick={() => setLocation(l)}
                className={`px-4 py-4 rounded-md font-bold text-base transition-all ${
                  location === l
                    ? l === "MPS"
                      ? "bg-[#0a2952] text-white shadow-md"
                      : "bg-[#14b8a6] text-white shadow-md"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !containerNo.trim()}
          data-testid="entry-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#ff5b2c] hover:bg-[#e54f24] disabled:opacity-40 disabled:cursor-not-allowed text-white py-4 rounded-md font-bold text-base shadow transition-all hover:-translate-y-[1px]"
        >
          {submitting ? (
            <><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> Adding...</>
          ) : (
            <><Check size={18} /> Add Container</>
          )}
        </button>
        <div className="text-xs text-slate-400 text-center">
          Tip: press <kbd className="px-1.5 py-0.5 border border-slate-300 rounded text-[10px] font-mono-tabular">Enter</kbd> to add and continue typing the next one.
        </div>
      </form>

      {/* Recently added in this session */}
      {recent.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="entry-recent">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="overline">Just added — this session</div>
              <div className="text-xs text-slate-500 mt-1">Tap the trash icon to undo a mistake.</div>
            </div>
            <span className="text-xs font-bold text-[#ff5b2c] font-mono-tabular">{recent.length}</span>
          </div>
          <ul>
            {recent.map((row) => (
              <li
                key={row.id}
                data-testid={`recent-row-${row.id}`}
                className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0 group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono-tabular text-base font-bold text-[#0a2952] truncate">{row.container_no}</span>
                  <span className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-bold tracking-wide bg-slate-100 text-[#0a2952] rounded-sm">{row.type_size}</span>
                  <span className={`hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-bold tracking-wide rounded-sm ${
                    row.location === "MPS" ? "bg-[#0a2952]/10 text-[#0a2952]" : "bg-[#14b8a6]/15 text-[#0d8b80]"
                  }`}>{row.location}</span>
                  <span className="text-xs text-slate-400 font-mono-tabular">{row.date}</span>
                </div>
                <button
                  type="button"
                  data-testid={`undo-btn-${row.id}`}
                  onClick={() => undoLast(row)}
                  className="ml-3 w-8 h-8 grid place-items-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                  title="Undo"
                >
                  <RotateCcw size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Empty session hint */}
      {recent.length === 0 && (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md px-5 py-8 text-center text-sm text-slate-500">
          No containers added yet in this session. Start typing the first one above.
          <div className="mt-2">
            <Link to="/records" className="text-xs text-[#ff5b2c] font-semibold hover:underline">
              Or browse all records →
            </Link>
          </div>
        </div>
      )}

      {toast && (
        <div
          data-testid="toast"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md shadow-lg text-sm font-semibold flex items-center gap-2 ${
            toast.kind === "err" ? "bg-red-600 text-white" : "bg-[#0a2952] text-white"
          }`}
        >
          {toast.kind === "err" ? <AlertTriangle size={16} /> : <Check size={16} />}
          {toast.msg}
        </div>
      )}

      <ScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onResult={(num) => {
          const dup = recent.find(
            (r) => r.container_no === num && r.date === date,
          );
          if (dup) {
            showToast(`${num} already added today — skipped`, "err");
            setContainerNo("");
            return;
          }
          setContainerNo(num);
          showToast(`Scanned ${num}`);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
      />
    </div>
  );
}

function TallyCard({ label, value, sub, accent }) {
  return (
    <div className="relative bg-white border border-slate-200 rounded-md p-3 sm:p-4 overflow-hidden">
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent }} />
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate">{label}</div>
      <div className="font-display font-extrabold text-lg sm:text-2xl text-[#0a2952] mt-1 leading-none font-mono-tabular truncate">{value}</div>
      <div className="text-[10px] text-slate-400 mt-1 font-mono-tabular truncate">{sub}</div>
    </div>
  );
}
